import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { getCountries } from "../../MyApi/admin/countries/countries";
import { toast } from "react-toastify";
import Select from "react-select";
import Button from "../../Components/UI/buttonGlobal/index";
import { postRewards, showRewards, updateRewards } from "../../MyApi/rewards";
import { getCouponsAPI } from "../../MyApi/coupons";
import LoadingSpinner from "../../Components/UI/spinner";
import i18next from "i18next";
import LangSelectModal from "../../Components/UI/langselectmodal";
import { formatPrice } from "../../utils/formatPrice";

/* example api */

const WithdrawlModal = ({ isOpen, toggle, modalMode, modalId, activeTab, dashboardData }) => {
  const queryClient = useQueryClient();
  const lang = i18next.language;
  const [selectedLangCode, setSelectedLangCode] = useState(lang);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image: "",
    type: activeTab !=="exchange"? "prize":"coupon", // prize | coupon | wallet
    points_cost: 1,
    value_amount: 1,
    is_it_for_loyalty_points:activeTab !=="exchange"? activeTab ==="points" ?1:0 :0,
    is_it_for_spendings: activeTab !=="exchange"? activeTab ==="points" ?0:1 :0,
    country_id: "",
    is_active: 1,
    start_date: "",
    end_date: "",
    currency_factor:""
  });

  const { data, isLoading: countriesLoading } = useQuery("getCountries", () =>
    getCountries()
  );

  const countries = data?.data?.countries || [];
  const { data: couponData, isLoading: couponLoading } = useQuery(
    "getCuopons",
    () => getCouponsAPI()
  );
  const coupons =couponData?.data?.coupons ||[]
  const { data: showData, isLoading: isShowDataLoading } = useQuery(
    ["showRewards", modalId, selectedLangCode],
    () => showRewards(modalId, selectedLangCode),
    { enabled: !!modalId && modalMode === "edit" }
  );

  const showRewardData = showData?.data.reward || {};

  // Map dashboard data to form structure
  const mapDashboardDataToForm = (dashboardData) => {
    if (!dashboardData) return null;
    
    return {
      title: dashboardData.title || "",
      description: dashboardData.description || "",
      image: dashboardData.image || "",
      type: activeTab !== "exchange" ? "prize" : "coupon",
      points_cost: dashboardData.points_cost || 1,
      value_amount: formatPrice( dashboardData.country.currency_factor, dashboardData.value_amount)  || 1,
      is_it_for_loyalty_points: activeTab !== "exchange" ? (activeTab === "points" ? 1 : 0) : 0,
      is_it_for_spendings: activeTab !== "exchange" ? (activeTab === "points" ? 0 : 1) : 0,
      country_id: dashboardData.country?.id || "",
      is_active: dashboardData.is_active ? 1 : 0,
      start_date: dashboardData.start_date || "",
      end_date: dashboardData.end_date || "",
        currency_factor:   dashboardData.country.currency_factor      ,
    };
  };

  // Map individual reward data to form structure
  const mapRewardDataToForm = (rewardData) => {
    if (!rewardData) return null;
    
    return {
      title: rewardData.title || "",
      description: rewardData.description || "",
      image: rewardData.image || "",
      type: rewardData.type || (activeTab !== "exchange" ? "prize" : "coupon"),
      points_cost: rewardData.points_cost || 1,
      value_amount:formatPrice( rewardData.country.currency_factor, rewardData.value_amount)    || 1,
      coupon_id: rewardData.coupon?.id || "",
      is_it_for_loyalty_points: activeTab !== "exchange" ? (activeTab === "points" ? 1 : 0) : 0,
      is_it_for_spendings: activeTab !== "exchange" ? (activeTab === "points" ? 0 : 1) : 0,
      country_id: rewardData.country?.id || "",
      currency_factor:   rewardData.country.currency_factor      ,
      is_active: rewardData.is_active ? 1 : 0,
      start_date: rewardData.start_date || "",
      end_date: rewardData.end_date || "",
    };
  };

  useEffect(() => {
    if (modalMode === "edit") {
      if (activeTab === "exchange" && Object.values(showRewardData).length > 0) {
        // Use individual reward data for exchange tab
        const mappedData = mapRewardDataToForm(showRewardData);
        setFormData(mappedData);
      } else if (activeTab !== "exchange" && dashboardData) {
        // Use dashboard data for points/orders_amount tabs
        const mappedData = mapDashboardDataToForm(dashboardData);
        setFormData(mappedData);
      }
    } else if (modalMode === "add") {
      // Reset form for add mode
      setFormData({
        title: "",
        description: "",
        image: "",
        type: activeTab !=="exchange"? "prize":"coupon",
        points_cost: 1,
        value_amount: 1,
        is_it_for_loyalty_points:activeTab !=="exchange"? activeTab ==="points" ?1:0 :0,
        is_it_for_spendings: activeTab !=="exchange"? activeTab ==="points" ?0:1 :0,
        country_id: "",
        is_active: 1,
        start_date: "",
        end_date: "",
        currency_factor:""
      });
    }
  }, [showData, dashboardData, modalMode, activeTab, modalId]);

  const { mutate, isLoading } = useMutation({
    mutationKey: ["postRewards"],
    mutationFn: (postData) => postRewards(postData),

    onSuccess: () => {
      toast.success("تم الإنشاء بنجاح");
      queryClient.invalidateQueries("getRewardsDashboard");
      if(activeTab ==="exchange"){
        queryClient.invalidateQueries("getRewards")
      }
      toggle();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    },
  });

  const updateMutation = useMutation({
    mutationKey: ["updateRewards"],
    mutationFn: (postData) => updateRewards(postData, modalId),

    onSuccess: () => {
      toast.success("تم التحديث بنجاح");
      queryClient.invalidateQueries("getRewardsDashboard");
      if(activeTab ==="exchange"){
        queryClient.invalidateQueries("getRewards")
      }
      toggle();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    },
  });

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if(name ==="country_id"){
       const selectedIndex = e.target.selectedIndex;
       const selectedId = e.target.options[selectedIndex].id;
setFormData((prev)=>({...prev ,currency_factor:selectedId}))
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === "file" ? files[0] : value,
    }));
  };

  const handleCuoponChange =(selectedOptions)=>{
    setFormData((prev)=>({...prev,coupon_id:selectedOptions?.id}))
  }

  const handleSubmit = (e) => {
    e.preventDefault();

    const dataSend = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "image" && !(value instanceof File)) {
        return;
      }
      if (value !== null && value !== "") {
        dataSend.append(key, value);
      }
    });

    if (modalMode === "edit") {
      dataSend.append("_method", "PUT");
      updateMutation.mutate(dataSend);
    } else {
      mutate(dataSend);
    }
  };

  if ((isShowDataLoading && modalMode === "edit" && activeTab === "exchange") || 
      (modalMode === "edit" && activeTab !== "exchange" && !dashboardData)) {
    return <LoadingSpinner />;
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered className="add">
      <ModalHeader toggle={toggle}>
        {activeTab ==="exchange" ?   
          (modalMode === "edit" ? "تعديل المكافأة" : "إضافة مكافأة جديدة")  
          :
          (modalMode === "edit" ? "تعديل سحب" : "إضافة سحب")
        }
      </ModalHeader>

      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <div className="row g-3">
            <div className="col-md-6">
              <FormGroup>
                <Label>اسم المكافأة</Label>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
            </div>

            <div className="col-md-6">
              <FormGroup>
                <Label>نوع السحب</Label>
                <Input
                  type="select"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                >
                 {activeTab ==="exchange"?null: <option value="prize">جائزة</option>}
                  {activeTab ==="exchange"? <>
                  <option value="coupon">كوبون</option>
                  <option value="wallet">رصيد</option>
                  </> :null}
                </Input>
              </FormGroup>
            </div>

            <div className="col-12">
              <FormGroup>
                <Label>الوصف</Label>
                <Input
                  type="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </FormGroup>
            </div>

            <div className="col-md-6">
              <FormGroup>
                <Label>تكلفة النقاط</Label>
                <Input
                  type="number"
                  name="points_cost"
                  value={formData.points_cost}
                  onChange={handleChange}
                  min={1}
                />
              </FormGroup>
            </div>

            <div className="col-12">
              <FormGroup>
                <Label>الصورة</Label>
                <input type="file" name="image" onChange={handleChange} />
              </FormGroup>

              {modalMode === "edit" && formData.image && typeof formData.image === 'string' && (
                <img src={formData.image} width={100} height={100} />
              )}
            </div>
            {formData.type !== "coupon" && (
              <div className="col-md-6">
                <FormGroup>
                  <Label>قيمه المبلغ </Label>
                  <Input
                    type="number"
                    name="value_amount"
                    value={formData.value_amount}
                    onChange={handleChange}
                    min={1}
                  />
                </FormGroup>
              </div>
            )}

            {formData.type === "coupon" && (
              <div className="col-md-6">
                <label htmlFor="" className="form-label">الكوبونات</label>
                <Select 
                  options={coupons} 
                  getOptionLabel={(e)=>e.code}   
                  getOptionValue={(e)=>e.id}  
                  onChange={handleCuoponChange}
                  value={coupons.find(c => c.id === formData.coupon_id)}
                />
              </div>
            )}

            <div className="col-md-6">
              <FormGroup>
                <Label>الدولة</Label>
                <Input
                  type="select"
                  name="country_id"
              
                  value={formData.country_id}
                  onChange={handleChange}
                  disabled={countriesLoading}
                >
                  <option value="">اختر الدولة</option>
                  {countries.map((c) => (
                    <option key={c.id} value={c.id} id={c.currency_factor}>
                      {c.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </div>

            <div className="col-md-6">
              <FormGroup>
                <Label>الحالة</Label>
                <Input
                  type="select"
                  name="is_active"
                  value={formData.is_active}
                  onChange={handleChange}
                >
                  <option value={1}>نشط</option>
                  <option value={0}>غير نشط</option>
                </Input>
              </FormGroup>
            </div>

            <div className="col-md-6">
              <FormGroup>
                <Label> تاريخ البداية</Label>
                <Input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                />
              </FormGroup>
            </div>

            <div className="col-md-6">
              <FormGroup>
                <Label> تاريخ الانتهاء</Label>
                <Input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                />
              </FormGroup>
            </div>

            <div className="col-md-6">
              <LangSelectModal 
                handleChange={(e) => setSelectedLangCode(e.target.value)}
                selectedLangCode={selectedLangCode}
              />
            </div>
          </div>
        </ModalBody>

        <ModalFooter className="d-flex align-items-center flex-nowrap ">
          <button
            className="btn btn-white"
            onClick={toggle}
            disabled={isLoading || updateMutation.isLoading}
          >
            إلغاء
          </button>
          
          <Button
            className="btn-primary"
            isLoading={isLoading || updateMutation.isLoading}
            disabled={isLoading || updateMutation.isLoading}
            label={modalMode === "edit" ? "تحديث" : "إضافة"}
            onClick={handleSubmit}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default WithdrawlModal;
